import { useState } from 'react';
import { 
  ChevronDown, Star, BarChart3, Activity, 
  Settings, RefreshCw, Bell, BookOpen,
  Info, Check, X, Loader2, ArrowUpDown, History
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

interface Order {
  price: number;
  amount: number;
  total: number;
  type: 'buy' | 'sell';
}

interface Trade {
  price: number;
  amount: number;
  time: string;
  type: 'buy' | 'sell';
}

interface OrderHistory {
  id: string;
  pair: string;
  side: 'buy' | 'sell';
  type: string;
  price: number;
  amount: number;
  total: number;
  status: 'filled' | 'partial' | 'open' | 'cancelled';
  time: string;
}

const SpotTradingPage = () => {
  const [activeTab, setActiveTab] = useState<'chart' | 'orderbook' | 'trades' | 'orders'>('chart');
  const [orderType, setOrderType] = useState<'limit' | 'market' | 'stop'>('limit');
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [timeframe, setTimeframe] = useState('1H');
  const [price, setPrice] = useState('96750.45');
  const [amount, setAmount] = useState('');
  const [isFavorite, setIsFavorite] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [orderHistory, setOrderHistory] = useState<OrderHistory[]>([]);
  const [showOrderSuccess, setShowOrderSuccess] = useState(false);
  const [lastOrder, setLastOrder] = useState<OrderHistory | null>(null);
  const [availableBalance, setAvailableBalance] = useState({ usdt: 12456.78, btc: 0.5234 });

  const chartData = [
    { time: '09:00', price: 96200, volume: 120 },
    { time: '09:15', price: 96450, volume: 85 },
    { time: '09:30', price: 96300, volume: 150 },
    { time: '09:45', price: 96600, volume: 200 },
    { time: '10:00', price: 96800, volume: 180 },
    { time: '10:15', price: 96700, volume: 95 },
    { time: '10:30', price: 96950, volume: 220 },
    { time: '10:45', price: 96850, volume: 140 },
    { time: '11:00', price: 97000, volume: 300 },
    { time: '11:15', price: 96900, volume: 160 },
    { time: '11:30', price: 97100, volume: 250 },
    { time: '11:45', price: 97050, volume: 180 },
    { time: '12:00', price: 97200, volume: 320 },
    { time: '12:15', price: 97150, volume: 190 },
    { time: '12:30', price: 97300, volume: 280 },
    { time: '12:45', price: 97250, volume: 150 },
    { time: '13:00', price: 97400, volume: 350 },
    { time: '13:15', price: 97350, volume: 200 },
    { time: '13:30', price: 97500, volume: 400 },
    { time: '13:45', price: 97450, volume: 220 },
    { time: '14:00', price: 97600, volume: 380 },
    { time: '14:15', price: 97550, volume: 240 },
    { time: '14:30', price: 97700, volume: 420 },
    { time: '14:45', price: 97650, volume: 260 },
    { time: '15:00', price: 97800, volume: 450 },
    { time: '15:15', price: 97750, volume: 280 },
    { time: '15:30', price: 97900, volume: 380 },
    { time: '15:45', price: 97850, volume: 300 },
    { time: '16:00', price: 98000, volume: 500 },
    { time: '16:15', price: 97950, volume: 320 },
    { time: '16:30', price: 98100, volume: 420 },
    { time: '16:45', price: 98050, volume: 340 },
    { time: '17:00', price: 98200, volume: 480 },
    { time: '17:15', price: 98150, volume: 360 },
    { time: '17:30', price: 98300, volume: 450 },
    { time: '17:45', price: 98250, volume: 380 },
    { time: '18:00', price: 98400, volume: 520 },
    { time: '18:15', price: 98350, volume: 400 },
    { time: '18:30', price: 98500, volume: 480 },
    { time: '18:45', price: 98450, volume: 420 },
    { time: '19:00', price: 98600, volume: 550 },
    { time: '19:15', price: 98550, volume: 440 },
    { time: '19:30', price: 98700, volume: 500 },
    { time: '19:45', price: 98650, volume: 460 },
    { time: '20:00', price: 98800, volume: 580 },
    { time: '20:15', price: 98750, volume: 480 },
    { time: '20:30', price: 98900, volume: 520 },
    { time: '20:45', price: 98850, volume: 500 },
    { time: '21:00', price: 99000, volume: 600 },
    { time: '21:15', price: 98950, volume: 520 },
    { time: '21:30', price: 99100, volume: 580 },
    { time: '21:45', price: 99050, volume: 540 },
    { time: '22:00', price: 99200, volume: 620 },
    { time: '22:15', price: 99150, volume: 560 },
    { time: '22:30', price: 99300, volume: 600 },
    { time: '22:45', price: 99250, volume: 580 },
    { time: '23:00', price: 99400, volume: 650 },
    { time: '23:15', price: 99350, volume: 600 },
    { time: '23:30', price: 99500, volume: 680 },
    { time: '23:45', price: 99450, volume: 620 },
    { time: '00:00', price: 99600, volume: 700 },
    { time: '00:15', price: 99550, volume: 640 },
    { time: '00:30', price: 99700, volume: 720 },
    { time: '00:45', price: 99650, volume: 660 },
    { time: '01:00', price: 99800, volume: 750 },
    { time: '01:15', price: 99750, volume: 680 },
    { time: '01:30', price: 99900, volume: 780 },
    { time: '01:45', price: 99850, volume: 700 },
    { time: '02:00', price: 100000, volume: 800 },
    { time: '02:15', price: 99950, volume: 720 },
    { time: '02:30', price: 100100, volume: 820 },
    { time: '02:45', price: 100050, volume: 740 },
    { time: '03:00', price: 100200, volume: 850 },
    { time: '03:15', price: 100150, volume: 760 },
    { time: '03:30', price: 100300, volume: 880 },
    { time: '03:45', price: 100250, volume: 780 },
    { time: '04:00', price: 100400, volume: 900 },
    { time: '04:15', price: 100350, volume: 800 },
    { time: '04:30', price: 100500, volume: 920 },
    { time: '04:45', price: 100450, volume: 820 },
    { time: '05:00', price: 100600, volume: 950 },
    { time: '05:15', price: 100550, volume: 840 },
    { time: '05:30', price: 100700, volume: 980 },
    { time: '05:45', price: 100650, volume: 860 },
    { time: '06:00', price: 100800, volume: 1000 },
    { time: '06:15', price: 100750, volume: 880 },
    { time: '06:30', price: 100900, volume: 1020 },
    { time: '06:45', price: 100850, volume: 900 },
    { time: '07:00', price: 101000, volume: 1050 },
    { time: '07:15', price: 100950, volume: 920 },
    { time: '07:30', price: 101100, volume: 1080 },
    { time: '07:45', price: 101050, volume: 940 },
    { time: '08:00', price: 101200, volume: 1100 },
    { time: '08:15', price: 101150, volume: 960 },
    { time: '08:30', price: 101300, volume: 1120 },
    { time: '08:45', price: 101250, volume: 980 },
    { time: '09:00', price: 96750, volume: 1150 },
  ];

  const orderBook: Order[] = [
    { price: 96755.00, amount: 0.5234, total: 50642.50, type: 'sell' },
    { price: 96754.50, amount: 1.2345, total: 119442.30, type: 'sell' },
    { price: 96754.00, amount: 0.8765, total: 84805.50, type: 'sell' },
    { price: 96753.50, amount: 2.3456, total: 226950.80, type: 'sell' },
    { price: 96753.00, amount: 0.4567, total: 44187.50, type: 'sell' },
    { price: 96752.50, amount: 1.5678, total: 151687.50, type: 'sell' },
    { price: 96752.00, amount: 0.7890, total: 76337.30, type: 'sell' },
    { price: 96751.50, amount: 3.4567, total: 334437.50, type: 'sell' },
    { price: 96751.00, amount: 0.2345, total: 22687.50, type: 'sell' },
    { price: 96750.50, amount: 1.8901, total: 182862.50, type: 'sell' },
    { price: 96750.00, amount: 5.6789, total: 549432.80, type: 'sell' },
    { price: 96749.50, amount: 0.1234, total: 11937.50, type: 'buy' },
    { price: 96749.00, amount: 2.3456, total: 226937.50, type: 'buy' },
    { price: 96748.50, amount: 0.5678, total: 54937.50, type: 'buy' },
    { price: 96748.00, amount: 1.2345, total: 119437.50, type: 'buy' },
    { price: 96747.50, amount: 0.8901, total: 86137.50, type: 'buy' },
    { price: 96747.00, amount: 3.4567, total: 334437.50, type: 'buy' },
    { price: 96746.50, amount: 0.4567, total: 44187.50, type: 'buy' },
    { price: 96746.00, amount: 1.6789, total: 162437.50, type: 'buy' },
    { price: 96745.50, amount: 0.7890, total: 76337.50, type: 'buy' },
    { price: 96745.00, amount: 4.5678, total: 441937.50, type: 'buy' },
  ];

  const recentTrades: Trade[] = [
    { price: 96750.45, amount: 0.1234, time: '14:32:15', type: 'buy' },
    { price: 96750.40, amount: 0.5678, time: '14:32:12', type: 'sell' },
    { price: 96750.50, amount: 0.2345, time: '14:32:08', type: 'buy' },
    { price: 96750.35, amount: 1.2345, time: '14:32:05', type: 'sell' },
    { price: 96750.55, amount: 0.4567, time: '14:32:01', type: 'buy' },
    { price: 96750.30, amount: 0.7890, time: '14:31:58', type: 'sell' },
    { price: 96750.60, amount: 0.3456, time: '14:31:55', type: 'buy' },
    { price: 96750.25, amount: 2.3456, time: '14:31:52', type: 'sell' },
    { price: 96750.65, amount: 0.5678, time: '14:31:48', type: 'buy' },
    { price: 96750.20, amount: 1.5678, time: '14:31:45', type: 'sell' },
    { price: 96750.70, amount: 0.2345, time: '14:31:42', type: 'buy' },
    { price: 96750.15, amount: 0.8901, time: '14:31:38', type: 'sell' },
    { price: 96750.75, amount: 0.4567, time: '14:31:35', type: 'buy' },
    { price: 96750.10, amount: 3.4567, time: '14:31:32', type: 'sell' },
    { price: 96750.80, amount: 0.1234, time: '14:31:28', type: 'buy' },
    { price: 96750.05, amount: 1.2345, time: '14:31:25', type: 'sell' },
    { price: 96750.85, amount: 0.6789, time: '14:31:22', type: 'buy' },
    { price: 96750.00, amount: 2.5678, time: '14:31:18', type: 'sell' },
    { price: 96750.90, amount: 0.3456, time: '14:31:15', type: 'buy' },
    { price: 96749.95, amount: 0.7890, time: '14:31:12', type: 'sell' },
  ];

  const calculateTotal = () => {
    const p = orderType === 'market' ? parseFloat('96750.45') : parseFloat(price) || 0;
    const a = parseFloat(amount) || 0;
    return (p * a).toFixed(2);
  };

  const handleSetPercentage = (pct: number) => {
    const balance = side === 'buy' ? availableBalance.usdt : availableBalance.btc;
    const currentPrice = orderType === 'market' ? 96750.45 : parseFloat(price);
    
    if (side === 'buy') {
      const maxAmount = balance / currentPrice;
      setAmount((maxAmount * (pct / 100)).toFixed(6));
    } else {
      setAmount((balance * (pct / 100)).toFixed(6));
    }
  };

  const handleSubmitOrder = () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast({ title: 'Error', description: 'Please enter a valid amount', variant: 'destructive' });
      return;
    }
    
    const total = parseFloat(calculateTotal());
    
    if (side === 'buy' && total > availableBalance.usdt) {
      toast({ title: 'Error', description: 'Insufficient USDT balance', variant: 'destructive' });
      return;
    }
    
    if (side === 'sell' && parseFloat(amount) > availableBalance.btc) {
      toast({ title: 'Error', description: 'Insufficient BTC balance', variant: 'destructive' });
      return;
    }
    
    setShowConfirmModal(true);
  };

  const confirmOrder = () => {
    setIsSubmitting(true);
    
    setTimeout(() => {
      const newOrder: OrderHistory = {
        id: `ORD${Date.now()}`,
        pair: 'BTC/USDT',
        side,
        type: orderType,
        price: orderType === 'market' ? 96750.45 : parseFloat(price),
        amount: parseFloat(amount),
        total: parseFloat(calculateTotal()),
        status: Math.random() > 0.3 ? 'filled' : 'partial',
        time: new Date().toLocaleTimeString(),
      };
      
      setOrderHistory([newOrder, ...orderHistory]);
      setLastOrder(newOrder);
      
      // Update balance
      if (side === 'buy') {
        setAvailableBalance(prev => ({
          usdt: prev.usdt - newOrder.total,
          btc: prev.btc + newOrder.amount
        }));
      } else {
        setAvailableBalance(prev => ({
          usdt: prev.usdt + newOrder.total,
          btc: prev.btc - newOrder.amount
        }));
      }
      
      setShowConfirmModal(false);
      setShowOrderSuccess(true);
      setAmount('');
      setIsSubmitting(false);
      
      toast({ 
        title: 'Order Executed!', 
        description: `Successfully ${side === 'buy' ? 'bought' : 'sold'} ${newOrder.amount} BTC at ${newOrder.price.toLocaleString()} USDT` 
      });
    }, 1500);
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      filled: 'bg-emerald-500/20 text-emerald-500',
      partial: 'bg-amber-500/20 text-amber-500',
      open: 'bg-blue-500/20 text-blue-500',
      cancelled: 'bg-red-500/20 text-red-500',
    };
    return (
      <Badge className={styles[status] || 'bg-muted'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  return (
    <div className="min-h-screen bg-background pt-20">
      {/* Header */}
      <div className="border-b border-border bg-background">
        <div className="max-w-[1920px] mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsFavorite(!isFavorite)}
                  className={`transition-colors ${isFavorite ? 'text-primary' : 'text-muted-foreground hover:text-primary'}`}
                >
                  <Star className={`w-5 h-5 ${isFavorite ? 'fill-primary' : ''}`} />
                </button>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground font-bold text-sm">
                    B
                  </div>
                  <div>
                    <span className="text-foreground font-bold">BTC</span>
                    <span className="text-muted-foreground mx-1">/</span>
                    <span className="text-muted-foreground">USDT</span>
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </div>

              <div className="hidden lg:flex items-center gap-6">
                <div>
                  <span className="text-2xl font-bold text-foreground">96,750.45</span>
                  <span className="text-emerald-500 ml-2">+2.34%</span>
                </div>
                <div className="h-8 w-px bg-muted" />
                <div>
                  <span className="text-xs text-muted-foreground block">24h High</span>
                  <span className="text-foreground font-mono">98,200.00</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block">24h Low</span>
                  <span className="text-foreground font-mono">94,500.00</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block">24h Volume (BTC)</span>
                  <span className="text-foreground font-mono">28,456.78</span>
                </div>
                <div>
                  <span className="text-xs text-muted-foreground block">24h Volume (USDT)</span>
                  <span className="text-foreground font-mono">2.75B</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button 
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => toast({ title: 'Refreshed', description: 'Market data updated' })}
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button 
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => toast({ title: 'Price Alert', description: 'You will be notified when price reaches your target' })}
              >
                <Bell className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-[1920px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
          {/* Chart & Order Book Section */}
          <div className="lg:col-span-9 border-r border-border">
            {/* Tabs */}
            <div className="flex items-center border-b border-border">
              {[
                { key: 'chart', label: 'Chart', icon: BarChart3 },
                { key: 'orderbook', label: 'Order Book', icon: BookOpen },
                { key: 'trades', label: 'Recent Trades', icon: Activity },
                { key: 'orders', label: 'My Orders', icon: History },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
                    activeTab === tab.key
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                  {tab.key === 'orders' && orderHistory.length > 0 && (
                    <Badge variant="secondary" className="ml-1 text-xs">{orderHistory.length}</Badge>
                  )}
                </button>
              ))}

              <div className="ml-auto flex items-center gap-2 px-4">
                {['1m', '5m', '15m', '1H', '4H', '1D', '1W'].map((tf) => (
                  <button
                    key={tf}
                    onClick={() => setTimeframe(tf)}
                    className={`px-3 py-1 rounded text-xs ${
                      timeframe === tf
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {tf}
                  </button>
                ))}
              </div>
            </div>

            {/* Content */}
            <div className="h-[500px]">
              {activeTab === 'chart' && (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="colorPrice" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(142 76% 36%)" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="hsl(142 76% 36%)" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="time" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} domain={['dataMin - 1000', 'dataMax + 1000']} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                      itemStyle={{ color: 'hsl(var(--foreground))' }}
                    />
                    <Area type="monotone" dataKey="price" stroke="hsl(142 76% 36%)" fillOpacity={1} fill="url(#colorPrice)" />
                  </AreaChart>
                </ResponsiveContainer>
              )}

              {activeTab === 'orderbook' && (
                <div className="grid grid-cols-2 h-full">
                  {/* Asks (Sell Orders) */}
                  <div className="border-r border-border">
                    <div className="flex items-center justify-between px-4 py-2 bg-card">
                      <span className="text-xs text-muted-foreground">Price (USDT)</span>
                      <span className="text-xs text-muted-foreground">Amount (BTC)</span>
                      <span className="text-xs text-muted-foreground">Total</span>
                    </div>
                    <div className="overflow-auto h-[calc(100%-40px)]">
                      {orderBook.filter(o => o.type === 'sell').reverse().map((order, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-1 hover:bg-card cursor-pointer" onClick={() => { setPrice(order.price.toFixed(2)); setSide('buy'); }}>
                          <span className="text-red-500 font-mono text-sm">{order.price.toFixed(2)}</span>
                          <span className="text-foreground font-mono text-sm">{order.amount.toFixed(4)}</span>
                          <span className="text-muted-foreground font-mono text-sm">{order.total.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Bids (Buy Orders) */}
                  <div>
                    <div className="flex items-center justify-between px-4 py-2 bg-card">
                      <span className="text-xs text-muted-foreground">Price (USDT)</span>
                      <span className="text-xs text-muted-foreground">Amount (BTC)</span>
                      <span className="text-xs text-muted-foreground">Total</span>
                    </div>
                    <div className="overflow-auto h-[calc(100%-40px)]">
                      {orderBook.filter(o => o.type === 'buy').map((order, i) => (
                        <div key={i} className="flex items-center justify-between px-4 py-1 hover:bg-card cursor-pointer" onClick={() => { setPrice(order.price.toFixed(2)); setSide('sell'); }}>
                          <span className="text-emerald-500 font-mono text-sm">{order.price.toFixed(2)}</span>
                          <span className="text-foreground font-mono text-sm">{order.amount.toFixed(4)}</span>
                          <span className="text-muted-foreground font-mono text-sm">{order.total.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'trades' && (
                <div className="h-full overflow-auto">
                  <div className="flex items-center justify-between px-4 py-2 bg-card sticky top-0">
                    <span className="text-xs text-muted-foreground">Time</span>
                    <span className="text-xs text-muted-foreground">Price (USDT)</span>
                    <span className="text-xs text-muted-foreground">Amount (BTC)</span>
                  </div>
                  {recentTrades.map((trade, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-1 hover:bg-card">
                      <span className="text-muted-foreground font-mono text-sm">{trade.time}</span>
                      <span className={`font-mono text-sm ${trade.type === 'buy' ? 'text-emerald-500' : 'text-red-500'}`}>
                        {trade.price.toFixed(2)}
                      </span>
                      <span className="text-foreground font-mono text-sm">{trade.amount.toFixed(4)}</span>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'orders' && (
                <div className="h-full overflow-auto p-4">
                  {orderHistory.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                      <History className="w-12 h-12 mb-4 opacity-50" />
                      <p>No orders yet</p>
                      <p className="text-sm">Your order history will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {orderHistory.map((order) => (
                        <div key={order.id} className="flex items-center justify-between p-3 bg-card rounded-lg border border-border">
                          <div className="flex items-center gap-4">
                            <div className={cn(
                              "w-10 h-10 rounded-lg flex items-center justify-center",
                              order.side === 'buy' ? 'bg-emerald-500/10' : 'bg-red-500/10'
                            )}>
                              <ArrowUpDown className={cn(
                                "w-5 h-5",
                                order.side === 'buy' ? 'text-emerald-500' : 'text-red-500'
                              )} />
                            </div>
                            <div>
                              <p className="font-medium">{order.pair}</p>
                              <p className="text-xs text-muted-foreground">
                                {order.type.toUpperCase()} • {order.time}
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-mono">{order.amount.toFixed(6)} BTC</p>
                            <p className="text-xs text-muted-foreground">@{order.price.toLocaleString()}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono">{order.total.toFixed(2)} USDT</p>
                            {getStatusBadge(order.status)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Order Form Section */}
          <div className="lg:col-span-3 bg-background">
            {/* Order Type Tabs */}
            <div className="flex border-b border-border">
              {['limit', 'market', 'stop'].map((type) => (
                <button
                  key={type}
                  onClick={() => setOrderType(type as any)}
                  className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
                    orderType === type
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>

            {/* Buy/Sell Tabs */}
            <div className="flex p-4 gap-2">
              <button
                onClick={() => setSide('buy')}
                className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                  side === 'buy'
                    ? 'bg-[#0ECB81] text-white'
                    : 'bg-card text-muted-foreground hover:text-foreground'
                }`}
              >
                Buy BTC
              </button>
              <button
                onClick={() => setSide('sell')}
                className={`flex-1 py-3 rounded-lg font-medium transition-colors ${
                  side === 'sell'
                    ? 'bg-[#F6465D] text-white'
                    : 'bg-card text-muted-foreground hover:text-foreground'
                }`}
              >
                Sell BTC
              </button>
            </div>

            {/* Order Form */}
            <div className="p-4 space-y-4">
              {orderType !== 'market' && (
                <div>
                  <label className="text-xs text-muted-foreground block mb-2">Price</label>
                  <div className="relative">
                    <input
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      className="w-full bg-card border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:border-primary"
                      placeholder="0.00"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">USDT</span>
                  </div>
                </div>
              )}

              <div>
                <label className="text-xs text-muted-foreground block mb-2">Amount</label>
                <div className="relative">
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="w-full bg-card border border-border rounded-lg px-4 py-3 text-foreground focus:outline-none focus:border-primary"
                    placeholder="0.00"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">BTC</span>
                </div>
              </div>

              {/* Percentage Buttons */}
              <div className="flex gap-2">
                {['25%', '50%', '75%', '100%'].map((pct) => (
                  <button
                    key={pct}
                    onClick={() => handleSetPercentage(parseInt(pct))}
                    className="flex-1 py-2 bg-card rounded text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    {pct}
                  </button>
                ))}
              </div>

              {/* Total */}
              <div className="flex justify-between items-center py-3 border-t border-border">
                <span className="text-muted-foreground">Total</span>
                <span className="text-foreground font-mono">{calculateTotal()} USDT</span>
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmitOrder}
                disabled={!amount || parseFloat(amount) <= 0}
                className={`w-full py-4 rounded-lg font-bold text-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                  side === 'buy'
                    ? 'bg-[#0ECB81] hover:bg-[#0ECB81]/90 text-white'
                    : 'bg-[#F6465D] hover:bg-[#F6465D]/90 text-white'
                }`}
              >
                {side === 'buy' ? 'Buy BTC' : 'Sell BTC'}
              </button>

              {/* Available Balance */}
              <div className="flex justify-between items-center text-sm">
                <span className="text-muted-foreground">Available</span>
                <div className="text-right">
                  <span className="text-foreground font-mono">{availableBalance.usdt.toFixed(2)} USDT</span>
                  <span className="text-muted-foreground mx-2">|</span>
                  <span className="text-foreground font-mono">{availableBalance.btc.toFixed(6)} BTC</span>
                </div>
              </div>
            </div>

            {/* Fee Info */}
            <div className="p-4 border-t border-border">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Info className="w-4 h-4" />
                <span>Trading Fee: 0.1% (Maker) / 0.1% (Taker)</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Order Confirmation Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpDown className="w-5 h-5 text-primary" />
              Confirm Order
            </DialogTitle>
            <DialogDescription>
              Please review your order details before confirming.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="p-4 bg-muted/50 rounded-lg space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Type</span>
                <span className={cn(
                  "font-medium",
                  side === 'buy' ? 'text-emerald-500' : 'text-red-500'
                )}>
                  {side === 'buy' ? 'Buy' : 'Sell'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Order Type</span>
                <span className="font-medium capitalize">{orderType}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Price</span>
                <span className="font-mono">{orderType === 'market' ? 'Market' : `${parseFloat(price).toLocaleString()} USDT`}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-mono">{amount} BTC</span>
              </div>
              <div className="border-t border-border pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="font-medium">Total</span>
                  <span className="font-mono font-bold">{calculateTotal()} USDT</span>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowConfirmModal(false)}>
                Cancel
              </Button>
              <Button 
                className={cn(
                  "flex-1",
                  side === 'buy' ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-red-500 hover:bg-red-600'
                )}
                onClick={confirmOrder}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Success Modal */}
      <Dialog open={showOrderSuccess} onOpenChange={setShowOrderSuccess}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-500">
              <Check className="w-6 h-6" />
              Order Executed!
            </DialogTitle>
            <DialogDescription>
              Your order has been successfully processed.
            </DialogDescription>
          </DialogHeader>
          {lastOrder && (
            <div className="space-y-4 pt-4">
              <div className="p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20 space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Order ID</span>
                  <span className="font-mono text-sm">{lastOrder.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Side</span>
                  <span className={cn(
                    "font-medium",
                    lastOrder.side === 'buy' ? 'text-emerald-500' : 'text-red-500'
                  )}>
                    {lastOrder.side === 'buy' ? 'Buy' : 'Sell'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Amount</span>
                  <span className="font-mono">{lastOrder.amount.toFixed(6)} BTC</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Price</span>
                  <span className="font-mono">{lastOrder.price.toLocaleString()} USDT</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total</span>
                  <span className="font-mono font-bold">{lastOrder.total.toFixed(2)} USDT</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  {getStatusBadge(lastOrder.status)}
                </div>
              </div>
              <Button className="w-full" onClick={() => { setShowOrderSuccess(false); setActiveTab('orders'); }}>
                <History className="w-4 h-4 mr-2" />
                View Order History
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SpotTradingPage;
